import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(
    "Notification" in window ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [supported, setSupported] = useState(false);
  const [ultimaFalha, setUltimaFalha] = useState("");

  useEffect(() => {
    setSupported("serviceWorker" in navigator && "PushManager" in window && "Notification" in window);
    setPermission("Notification" in window ? Notification.permission : "denied");
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return false;
    // Cada passo tem nome: se falhar, o servidor fica sabendo em qual foi.
    let etapa = "chave";
    try {
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.publicKey) {
        console.warn("[Push] VAPID_PUBLIC_KEY não configurado no servidor.");
        return false;
      }

      etapa = "service-worker";
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, recusar) =>
          setTimeout(() => recusar(new Error("service worker não ficou pronto em 15s")), 15000)
        ),
      ]);

      etapa = "consultar-inscricao";
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        etapa = "inscrever";
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      etapa = "gravar";
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscribed(true);
      return true;
    } catch (err) {
      console.error("[Push] Falha ao inscrever:", err);
      let app = "navegador";
      try {
        if (sessionStorage.getItem("floow:appAndroid") === "1" || document.referrer.startsWith("android-app://")) app = "android";
        else if (window.matchMedia("(display-mode: standalone)").matches) app = "instalado";
      } catch (e) { /* sem acesso */ }
      api
        .post("/push/falha", {
          etapa,
          nome: err && err.name,
          mensagem: err && err.message,
          permissao: "Notification" in window ? Notification.permission : "sem suporte",
          app,
        })
        .catch(() => {});
      setUltimaFalha(`${etapa}: ${(err && err.message) || "erro desconhecido"}`);
      return false;
    }
  }, [supported]);

  const requestAndSubscribe = useCallback(async () => {
    if (!supported) return "unsupported";
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === "granted") {
      const deuCerto = await subscribe();
      if (!deuCerto) return "falhou";
    }
    return result;
  }, [supported, subscribe]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error("[Push] Falha ao desinscrever:", err);
    }
  }, [supported]);

  // Verifica se já está inscrito ao carregar
  useEffect(() => {
    if (!supported || permission !== "granted") return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (sub) {
          setSubscribed(true);
          // Garantir que o backend tem essa subscription
          const subJson = sub.toJSON();
          api.post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [supported, permission]);

  return { supported, permission, subscribed, requestAndSubscribe, unsubscribe, ultimaFalha };
}
